import { useI18n } from 'vue-i18n';

// Números no formato do idioma da conta. Os formatadores só recebem número: o que
// é null passa por `orNoData` (ou pelo MetricValue) e vira "sem dado", nunca 0.
export function useAnalyticsFormat() {
  const { t, locale } = useI18n();
  const intlLocale = () => locale.value.replace('_', '-');

  const number = value => new Intl.NumberFormat(intlLocale()).format(value);

  // Recebe o percentual inteiro já arredondado (0–100).
  const percent = value =>
    new Intl.NumberFormat(intlLocale(), {
      style: 'percent',
      maximumFractionDigits: 0,
    }).format(value / 100);

  const seconds = value =>
    new Intl.NumberFormat(intlLocale(), {
      style: 'unit',
      unit: 'second',
      unitDisplay: 'narrow',
      maximumFractionDigits: 1,
    }).format(value);

  // Espera que passa de um minuto vai em minutos, horas e dias ("12 min",
  // "3 h 5 min", "2 dias 4 h"): em segundos, uma hora seria "3.600 s".
  const unit = (amount, name) =>
    new Intl.NumberFormat(intlLocale(), {
      style: 'unit',
      unit: name,
      unitDisplay: 'short',
    }).format(amount);

  const duration = value => {
    if (value < 60) return seconds(value);
    const minutes = Math.round(value / 60);
    if (minutes < 60) return unit(minutes, 'minute');
    const hours = Math.floor(minutes / 60);
    const [big, rest, restUnit] =
      hours < 24
        ? [unit(hours, 'hour'), minutes % 60, 'minute']
        : [unit(Math.floor(hours / 24), 'day'), hours % 24, 'hour'];
    return rest ? `${big} ${unit(rest, restUnit)}` : big;
  };

  // Custo de modelo é fração de centavo por conversa: duas casas mostrariam US$ 0,01
  // para quase tudo.
  const usd = value =>
    new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);

  // No fuso da conta, o mesmo em que o período é contado.
  const dateTime = (isoDate, timeZone) =>
    new Intl.DateTimeFormat(intlLocale(), {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(isoDate));

  // A hora do dia ("10:32") de quando um dado ao vivo foi lido, no fuso da conta
  // (o do período, regra da REPORTS-02); sem ele, no do navegador.
  const clock = (date, timeZone) =>
    new Intl.DateTimeFormat(intlLocale(), {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);

  const orNoData = (value, format) =>
    typeof value === 'number' ? format(value) : t('ANALYTICS_REPORTS.NO_DATA');

  return {
    number,
    percent,
    seconds,
    duration,
    usd,
    dateTime,
    clock,
    orNoData,
  };
}
